import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { form, minDate, required, submit, validate } from '@angular/forms/signals';

import { CustomIcon, IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  ElementActions,
  ElementTypes,
  FinnairThemeService,
  GaContext,
  ThirdPartyAncillariesProvider,
} from '@fcom/common';
import { ExpediaHotelOrigin } from '@fcom/common/enums';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { GtmService } from '@fcom/common/gtm';
import { WindowRef } from '@fcom/core';
import { LocalDate } from '@fcom/core/utils';
import { LanguageService } from '@fcom/ui-translate';
import {
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  DateLabels,
  DateRange,
  IconPosition,
  NotificationLayout,
  UiLabels,
} from '@fcom/ui-components';

import { GTM_BOOKING_WIDGET_ELEMENT_IDS, mapToGaState } from '../../../manage-booking/constants';
import type { HotelRoom, HotelSearchForm, LocationParam } from '../../interfaces';
import { hotelSearchDeepLink } from '../../utils/utils.hotel';

const CALENDAR_DAYS = 500;

const startOfToday = (): Date => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

@Component({
  selector: 'fin-hotel-search-widget',
  templateUrl: './hotel-search-widget.component.html',
  styleUrls: ['./hotel-search-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HotelSearchWidgetComponent {
  private languageService = inject(LanguageService);
  private windowRef = inject(WindowRef);
  private gtmService = inject(GtmService);
  private themeService = inject(FinnairThemeService);

  readonly isDarkTheme = computed(() => this.themeService.siteTheme() === FinnairTheme.DARK_THEME);

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly NotificationLayout = NotificationLayout;
  readonly CustomIcon = CustomIcon;
  readonly IconLibrary = IconLibrary;
  readonly IconPosition = IconPosition;

  readonly datepickerLabels$ = this.languageService.translate<UiLabels>('datepicker');
  readonly dateLabels$ = this.languageService.translate<DateLabels>('date');

  datesModalOpen = false;
  readonly activeDateField = signal<'checkIn' | 'checkOut' | null>(null);

  private readonly today = startOfToday();

  readonly model = signal<HotelSearchForm>({
    destination: null,
    checkIn: null,
    checkOut: null,
    rooms: [{ adults: 1, childAges: [] }],
  });

  readonly form = form(this.model, (path) => {
    required(path.destination);
    required(path.checkIn);
    required(path.checkOut);
    minDate(path.checkIn, this.today);
    minDate(path.checkOut, this.today);
    validate(path.checkOut, ({ value, valueOf }) => {
      const checkOut = value();
      const checkIn = valueOf(path.checkIn);
      return checkOut && checkIn && checkOut <= checkIn ? { kind: 'checkout-before-checkin' } : undefined;
    });
    validate(path.rooms, ({ value }) => {
      const invalid = value().some((room) => room.adults < 1 || room.childAges.some((age) => age === null));
      return invalid ? { kind: 'rooms-invalid' } : undefined;
    });
  });

  readonly checkInLocalDate = computed<LocalDate | undefined>(() => {
    const date = this.form.checkIn().value();
    return date ? LocalDate.forDateObj(date) : undefined;
  });

  readonly checkOutLocalDate = computed<LocalDate | undefined>(() => {
    const date = this.form.checkOut().value();
    return date ? LocalDate.forDateObj(date) : undefined;
  });

  readonly datepickerSelectedDates = computed<DateRange | [LocalDate] | undefined>(() => {
    const checkIn = this.checkInLocalDate();
    const checkOut = this.checkOutLocalDate();
    if (checkIn && checkOut) {
      return [checkIn, checkOut];
    }
    return checkIn ? [checkIn] : undefined;
  });

  readonly datepickerRange = computed<DateRange>(() => [LocalDate.now(), LocalDate.now().plusDays(CALENDAR_DAYS)]);

  openDateModal(field: 'checkIn' | 'checkOut'): void {
    this.activeDateField.set(field);
    this.datesModalOpen = true;
  }

  closeDatesModal(): void {
    this.datesModalOpen = false;
    this.activeDateField.set(null);
  }

  onDestinationSelected(event: LocationParam): void {
    this.form.destination().value.set(event.locations.origin ?? null);
  }

  onRoomsChange(rooms: HotelRoom[]): void {
    this.form.rooms().value.set(rooms);
  }

  updateDates([checkInString, checkOutString]: [string] | [string, string]): void {
    this.form.checkIn().value.set(checkInString ? new LocalDate(checkInString).toDate() : null);
    this.form.checkOut().value.set(checkOutString ? new LocalDate(checkOutString).toDate() : null);
  }

  async onSearch(): Promise<void> {
    await submit(this.form, async () => {
      this.openDeepLink(this.model());
    });
  }

  private openDeepLink(model: HotelSearchForm): void {
    const url = hotelSearchDeepLink(model, {
      lang: this.languageService.langValue,
      origin: ExpediaHotelOrigin.WIDGET_HOTEL_TAB,
    });

    this.gtmService.trackElement(
      GTM_BOOKING_WIDGET_ELEMENT_IDS[ThirdPartyAncillariesProvider.EXPEDIA_HOTEL],
      GaContext.THIRD_PARTY_ANCILLARY,
      ElementTypes.LINK,
      mapToGaState[ThirdPartyAncillariesProvider.EXPEDIA_HOTEL],
      ElementActions.CLICK
    );

    this.windowRef.nativeWindow.open(url, '_blank', 'noopener,noreferrer');
  }
}
